import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import {
  OWN_DROP_SELECTOR,
  isFileDrag,
  pastedImage,
  shouldHandleGlobalDrop,
} from '../../utils/globalDocumentDrop';

/** Any open dialog, drawer or menu. The page-wide drop stands aside while one is showing. */
function otherModalOpen(): boolean {
  return Boolean(document.querySelector('.MuiModal-root:not(.MuiModal-hidden)'));
}

/** A pasted screenshot arrives as "image.png"; name it so the case's Files list says what it was. */
export function namePastedImage(file: File): File {
  const extension = file.type.split('/')[1]?.replace('jpeg', 'jpg') || 'png';
  return new File([file], `pasted-${Date.now()}.${extension}`, { type: file.type });
}

/**
 * Drop or paste a document anywhere in the staff portal to open "Create case" with it.
 *
 * The rules for which drops it takes are in utils/globalDocumentDrop. The listeners are split by
 * phase on purpose. The capture phase sees every drag first, so it can hide the overlay over a zone
 * that takes its own files (some of those stop the event before it would reach the window). The
 * bubble phase runs after the page's own handlers, so a zone that claimed the drop has already said
 * so with preventDefault, and is left alone.
 */
const GlobalDocumentDrop: React.FC<{
  dialogOpen: boolean;
  onFile: (file: File, ignoredFiles: number) => void;
}> = ({ dialogOpen, onFile }) => {
  const [active, setActive] = React.useState(false);
  const dialogOpenRef = React.useRef(dialogOpen);
  dialogOpenRef.current = dialogOpen;

  React.useEffect(() => {
    let depth = 0;

    const context = () => ({ dialogOpen: dialogOpenRef.current, otherModalOpen: otherModalOpen() });
    const accepts = (event: DragEvent) =>
      shouldHandleGlobalDrop(
        {
          defaultPrevented: event.defaultPrevented,
          types: event.dataTransfer?.types,
          target: event.target instanceof Element ? event.target : null,
        },
        context(),
      );

    const onDragEnterCapture = (event: DragEvent) => {
      if (isFileDrag(event.dataTransfer?.types)) depth += 1;
    };
    const onDragLeaveCapture = (event: DragEvent) => {
      if (!isFileDrag(event.dataTransfer?.types)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setActive(false);
    };
    const onDragOverCapture = (event: DragEvent) => {
      if (!isFileDrag(event.dataTransfer?.types)) return;
      const target = event.target instanceof Element ? event.target : null;
      const { dialogOpen: open, otherModalOpen: modal } = context();
      if (open || modal || target?.closest(OWN_DROP_SELECTOR)) setActive(false);
    };
    const onDragOver = (event: DragEvent) => {
      if (!accepts(event)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      setActive(true);
    };
    const onDropCapture = () => {
      depth = 0;
      setActive(false);
    };
    const onDrop = (event: DragEvent) => {
      if (!accepts(event)) return;
      event.preventDefault();
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length > 0) onFile(files[0], files.length - 1);
    };
    const onPaste = (event: ClipboardEvent) => {
      if (dialogOpenRef.current) return; // the dialog takes its own pastes
      const files = Array.from(event.clipboardData?.files ?? []);
      const index = pastedImage(files, {
        focused: document.activeElement as HTMLElement | null,
        dialogOpen: false,
        otherModalOpen: otherModalOpen(),
      });
      if (index === null) return;
      event.preventDefault();
      onFile(namePastedImage(files[index]), 0);
    };

    window.addEventListener('dragenter', onDragEnterCapture, true);
    window.addEventListener('dragleave', onDragLeaveCapture, true);
    window.addEventListener('dragover', onDragOverCapture, true);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDropCapture, true);
    window.addEventListener('drop', onDrop);
    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('dragenter', onDragEnterCapture, true);
      window.removeEventListener('dragleave', onDragLeaveCapture, true);
      window.removeEventListener('dragover', onDragOverCapture, true);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDropCapture, true);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('paste', onPaste);
    };
  }, [onFile]);

  if (!active) return null;

  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        // Never in the way of the drop itself: the event must still reach whatever is underneath.
        pointerEvents: 'none',
        zIndex: (theme) => theme.zIndex.modal,
        bgcolor: 'rgba(25, 118, 210, 0.12)',
        border: '3px dashed',
        borderColor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper elevation={6} sx={{ px: 4, py: 3, maxWidth: 480, textAlign: 'center' }}>
        <NoteAddIcon color="primary" sx={{ fontSize: 48 }} />
        <Typography variant="h6">Drop to create a case from this document</Typography>
        <Typography variant="body2" color="text.secondary">
          It will be read and you&apos;ll check everything before anything is saved.
        </Typography>
      </Paper>
    </Box>
  );
};

export default GlobalDocumentDrop;
